<script>
  import { navigate } from '../router.js';
  export let slug;

  let info = null;
  let error = '';
  let directory = '';

  async function loadItem(s) {
    info = null;
    error = '';
    directory = `/portfolio/${s}`;
    try {
      const res = await fetch(`${directory}/info.json`);
      if (!res.ok) throw new Error('Item not found');
      info = await res.json();
    } catch (e) {
      error = e.message;
    }
  }

  $: loadItem(slug);

  let activeImage = 0;
  $: if (info) activeImage = 0;

  const LABELS = {
    materials: 'Materials',
    techniques: 'Techniques',
    inspiration: 'Inspiration',
    exhibitions: 'Exhibitions',
    awards: 'Awards',
  };
</script>

<svelte:head>
  {#if info}
    <title>{info.title} — Hanna Tiina Pekk</title>
    <meta name="description" content={info.description || `${info.title}, ${info.year}. Fashion design by Hanna Tiina Pekk.`} />
    <meta property="og:title"       content="{info.title} — Hanna Tiina Pekk" />
    <meta property="og:description" content={info.description || `${info.title}, ${info.year}. Fashion design by Hanna Tiina Pekk.`} />
    <meta property="og:type"        content="article" />
    <meta property="og:image"       content="https://hannatiinapekk.com/portfolio/{slug}/{info.images[0]}" />
    <meta name="twitter:title"       content="{info.title} — Hanna Tiina Pekk" />
    <meta name="twitter:description" content={info.description || `${info.title}, ${info.year}. Fashion design by Hanna Tiina Pekk.`} />
    <meta name="twitter:image"       content="https://hannatiinapekk.com/portfolio/{slug}/{info.images[0]}" />
    {@html `<script type="application/ld+json">${JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CreativeWork",
      "name": info.title,
      "description": info.description || undefined,
      "dateCreated": String(info.year),
      "image": `https://hannatiinapekk.com/portfolio/${slug}/${info.images[0]}`,
      "author": { "@type": "Person", "name": "Hanna Tiina Pekk" }
    })}</script>`}
  {/if}
</svelte:head>

<div class="product-view">
  {#if error}
    <div class="error">{error}</div>
  {:else if !info}
    <div class="loading">Loading…</div>
  {:else}
    <a href="/" class="back" on:click|preventDefault={() => history.length > 1 ? history.back() : navigate('/')}>← Back</a>

    <div class="layout">
      <!-- Image column -->
      <div class="images">
        <div class="main-img-wrap">
          <img
            src={`${directory}/${info.images[activeImage]}`}
            alt={info.title}
            class="main-img"
          />
        </div>
        {#if info.images.length > 1}
          <div class="thumbnails">
            {#each info.images as img, i}
              <button
                class="thumb-btn {i === activeImage ? 'active' : ''}"
                on:click={() => (activeImage = i)}
                aria-label="View image {i + 1}"
              >
                <img src={`${directory}/${img}`} alt="" />
              </button>
            {/each}
          </div>
        {/if}
      </div>

      <!-- Info column -->
      <div class="info">
        <h1 class="title">{info.title}</h1>
        <p class="year">{info.year}</p>

        {#if info.description}
          <p class="description">{info.description}</p>
        {/if}

        <dl class="meta">
          {#each Object.keys(LABELS) as key}
            {#if info[key]}
              <dt>{LABELS[key]}</dt>
              <dd>
                {#if Array.isArray(info[key])}
                  {info[key].join(', ')}
                {:else}
                  {info[key]}
                {/if}
              </dd>
            {/if}
          {/each}
        </dl>
      </div>
    </div>
  {/if}
</div>

<style>
  .product-view {
    max-width: 1100px;
    margin: 0 auto;
    padding: 2rem 1.5rem 4rem;
  }

  .back {
    display: inline-block;
    font-size: 0.75rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #888;
    text-decoration: none;
    margin-bottom: 2.5rem;
    transition: color 0.2s;
  }
  .back:hover { color: #1a1a1a; }

  .layout {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: start;
  }

  @media (max-width: 700px) {
    .layout { grid-template-columns: 1fr; gap: 2rem; }
  }

  /* Images */
  .main-img-wrap {
    width: 100%;
    aspect-ratio: 3 / 4;
    overflow: hidden;
    background: #f0ede9;
  }
  .main-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .thumbnails {
    display: flex;
    gap: 0.5rem;
    margin-top: 0.75rem;
    flex-wrap: wrap;
  }
  .thumb-btn {
    width: 64px;
    height: 80px;
    padding: 0;
    border: 2px solid transparent;
    background: none;
    cursor: pointer;
    overflow: hidden;
    transition: border-color 0.2s;
  }
  .thumb-btn img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .thumb-btn.active { border-color: #1a1a1a; }

  /* Info */
  .info {
    padding-top: 0.5rem;
  }

  .title {
    font-size: 1.6rem;
    font-weight: 400;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    margin: 0 0 0.4rem;
    color: #1a1a1a;
  }

  .year {
    font-size: 0.75rem;
    letter-spacing: 0.12em;
    color: #999;
    margin: 0 0 1.8rem;
  }

  .description {
    font-size: 0.85rem;
    line-height: 1.75;
    color: #444;
    margin: 0 0 2rem;
    max-width: 38ch;
  }

  .meta {
    display: grid;
    grid-template-columns: auto 1fr;
    column-gap: 1.5rem;
    row-gap: 0.6rem;
    margin: 0;
  }
  dt {
    font-size: 0.7rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #aaa;
    white-space: nowrap;
    padding-top: 0.05em;
  }
  dd {
    font-size: 0.8rem;
    color: #333;
    margin: 0;
    line-height: 1.5;
  }

  .loading, .error {
    font-size: 0.85rem;
    color: #888;
    padding: 4rem 0;
    text-align: center;
  }
</style>
